<template>
  <CRow class="mb-4">
    <CCol
      col="12"
      class="mb-2 d-flex justify-content-end align-items-center"
    >
      <CButton
        size="sm"
        color="transparent"
        @click="moveChartDate(-1, 'month')"
      >
        <CIcon name="cil-chevron-left" />
      </CButton>
      <span class="mx-2 font-weight-bold">{{ chartReferenceDate.format('MMMM YYYY') }}</span>
      <CButton
        size="sm"
        color="transparent"
        @click="moveChartDate(1, 'month')"
      >
        <CIcon name="cil-chevron-right" />
      </CButton>
    </CCol>

    <CCol md="4">
      <CCard>
        <CCardHeader>Total Open vs Paid</CCardHeader>
        <CCardBody class="p-2">
          <CChartBar
            :datasets="chartsData.openVsPaid.datasets"
            :labels="chartsData.openVsPaid.labels"
            :options="chartsData.openVsPaid.options"
            style="height: 150px;"
          />
        </CCardBody>
      </CCard>
    </CCol>
    <CCol md="4">
      <CCard>
        <CCardHeader>Count of Invoices By Status</CCardHeader>
        <CCardBody class="p-2">
          <CChartPie
            :datasets="chartsData.statusPie.datasets"
            :labels="chartsData.statusPie.labels"
            :options="chartsData.statusPie.options"
            style="height: 150px;"
          />
        </CCardBody>
      </CCard>
    </CCol>
    <CCol md="4">
      <CCard>
        <CCardHeader>Current Month vs Previous Month</CCardHeader>
        <CCardBody class="p-2">
          <CChartBar
            :datasets="chartsData.totalCount.datasets"
            :labels="chartsData.totalCount.labels"
            :options="chartsData.totalCount.options"
            style="height: 150px;"
          />
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import { CChartBar, CChartPie } from '@coreui/vue-chartjs'
import moment from 'moment'

export default {
  name: 'BuyerBillingCharts',
  components: {
    CChartBar,
    CChartPie
  },
  props: {
    invoices: {
      type: Array,
      default: () => []
    },
    enableFakeData: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartReferenceDate: moment()
    }
  },
  computed: {
    chartsData() {
      // Initialize with props data or empty array
      let chartInvoices = this.invoices ? [...this.invoices] : []
      
      // Use fake data if enabled
      if (this.enableFakeData) {
        chartInvoices = this.generateFakeInvoices()
      } else {
        // Filter out summary row (which typically has no billingCycleStartDate)
        // If billingCycleStartDate is missing, moment() defaults to current time, causing data to pile up in 'current month'
        chartInvoices = chartInvoices.filter(i => i.billingCycleStartDate)
      }

      // 1. Month Segments Logic (Same as before)
      const getMonthSegments = (date) => {
        const monthStart = date.clone().startOf('month')
        const monthEnd = date.clone().endOf('month')
        const segments = []
        
        let current = monthStart.clone()
        let nextSunday = current.day() !== 0 ? current.clone().day(7) : current.clone()

        segments.push({
            start: monthStart.clone(),
            end: nextSunday.clone(),
            label: nextSunday.format('MMM D')
        })

        let loopStart = nextSunday.clone().add(1, 'day')
        while (loopStart.isBefore(monthEnd)) {
            const loopEnd = loopStart.clone().add(6, 'days')
            if (loopEnd.isAfter(monthEnd)) {
                 segments.push({ start: loopStart.clone(), end: monthEnd.clone(), label: monthEnd.format('MMM D') })
                break
            } else {
                segments.push({ start: loopStart.clone(), end: loopEnd.clone(), label: loopEnd.format('MMM D') })
            }

            loopStart = loopEnd.clone().add(1, 'day')
        }

        return segments
      }

      const currentSegments = getMonthSegments(this.chartReferenceDate)
      const lastSegments = getMonthSegments(this.chartReferenceDate.clone().subtract(1, 'month'))

      // --- Buckets ---
      const openVsPaidBuckets = {
         paid: new Array(currentSegments.length).fill(0),
         open: new Array(currentSegments.length).fill(0) 
      }

      const statusBuckets = {
         paid: 0,
         unpaid: 0,
         overdue: 0
      }

      const totalCountBuckets = {
         current: new Array(currentSegments.length).fill(0),
         last: new Array(currentSegments.length).fill(0)
      }
      


      chartInvoices.forEach(invoice => {
        const date = moment(invoice.billingCycleStartDate)
        const amount = invoice.totalInvoicedAmount || 0
        const amountPaid = invoice.amountPaid || 0
        const status = invoice.paymentStatusId 

        const outstanding = (amount - amountPaid)

        // --- Current Month Logic ---
        const cMonthStart = currentSegments[0].start
        const cMonthEnd = currentSegments[currentSegments.length-1].end
        
        if (date.isBetween(cMonthStart, cMonthEnd, 'day', '[]')) {
             const segIndex = currentSegments.findIndex(s => date.isBetween(s.start, s.end, 'day', '[]'))
             if (segIndex !== -1) {
                 // Open vs Paid Bar (Weekly within Current Month)
                 openVsPaidBuckets.paid[segIndex] += amountPaid
                 if (outstanding > 0) {
                    openVsPaidBuckets.open[segIndex] += outstanding
                 }

                 // Legacy Total Count
                 totalCountBuckets.current[segIndex] += 1

                 // Status Pie Chart (Count-based)
                 if (status === 'Paid') {
                    statusBuckets.paid += 1
                 } else if (status === 'Overdue') {
                    statusBuckets.overdue += 1
                 } else if (status === 'Outstanding') {
                     statusBuckets.unpaid += 1
                 }
             }
        }
        
        // --- Last Month Logic (Only for Total Count Chart) ---
        const lMonthStart = lastSegments[0].start
        const lMonthEnd = lastSegments[lastSegments.length-1].end
        
        if (date.isBetween(lMonthStart, lMonthEnd, 'day', '[]')) {
             const segIndex = lastSegments.findIndex(s => date.isBetween(s.start, s.end, 'day', '[]'))
             if (segIndex !== -1 && segIndex < totalCountBuckets.last.length) {
                 totalCountBuckets.last[segIndex] += 1
             }
        }
      })

      // Chart Options
      const baseOptions = {
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { drawOnChartArea: false, drawBorder: true },
            ticks: { autoSkip: false, maxRotation: 0 }
          }],
          yAxes: [{
            gridLines: { drawOnChartArea: false, drawBorder: true },
            ticks: { maxTicksLimit: 2, beginAtZero: true }
          }]
        }
      }

      const getPercentages = (values, totals) => {
         return values.map((val, i) => {
             const total = totals[i]
             return total > 0 ? (val / total) * 100 : 0
         })
      }

      const totalPerBucket = openVsPaidBuckets.paid.map((p, i) => p + openVsPaidBuckets.open[i])
      const paidPercentages = getPercentages(openVsPaidBuckets.paid, totalPerBucket)
      const openPercentages = getPercentages(openVsPaidBuckets.open, totalPerBucket)

      // 100% Stacked Options
      const stacked100Options = {
        ...baseOptions,
        scales: {
          xAxes: [{
             ...baseOptions.scales.xAxes[0],
             stacked: true
          }],
          yAxes: [{
            ...baseOptions.scales.yAxes[0],
            stacked: true,
            ticks: {
              callback: (value) => value + '%',
              max: 100,
              maxTicksLimit: 5,
              beginAtZero: true
            }
          }]
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const rawData = dataset.rawMethod ? dataset.rawMethod() : [] 
              // We need the raw value. Since we prepended 0 to data, index matches.
              const rawVal = rawData[tooltipItem.index] || 0
              
              let label = dataset.label || ''
              if (label) {
                label += ': '
              }

              // Display $Amount (Percentage%)
              label += '$' + Number(rawVal).toLocaleString() + ' (' + Number(tooltipItem.yLabel).toFixed(1) + '%)'
              return label
            }
          }
        }
      }

      const labels = [currentSegments[0].start.format('MMM D'), ...currentSegments.map(s => s.label)]
      const prependZero = (arr) => [0, ...arr]

      return {
        openVsPaid: {
          labels,
          datasets: [
            {
              label: 'Paid',
              backgroundColor: '#2eb85c', 
              data: prependZero(paidPercentages),
              rawMethod: () => prependZero(openVsPaidBuckets.paid) // Access raw data for tooltip
            },
            {
              label: 'Open',
              backgroundColor: '#f9b115', 
              data: prependZero(openPercentages),
              rawMethod: () => prependZero(openVsPaidBuckets.open) // Access raw data for tooltip
            }
          ],
          options: {
              ...stacked100Options, // Use 100% stacked options
              legend: { display: false } 
          }
        },
        statusPie: {
          labels: ['Paid', 'Outstanding', 'Overdue'],
          datasets: [
            {
              backgroundColor: ['#2eb85c', '#f9b115', '#e55353'], // Green, Yellow, Red
              data: [statusBuckets.paid, statusBuckets.unpaid, statusBuckets.overdue]
            }
          ],
          options: {
             maintainAspectRatio: false,
             legend: { display: false }
          }
        },
        totalCount: {
          labels,
          datasets: [
            {
              label: 'Current Month',
              borderColor: '#39f', 
              backgroundColor: 'transparent',
              data: prependZero(totalCountBuckets.current),
              fill: false,
              borderWidth: 2,
              pointRadius: 2,
              type: 'line' 
            },
            {
              label: 'Last Month',
              backgroundColor: 'rgba(206, 210, 216, 0.5)', 
              borderColor: 'transparent',
              data: prependZero(totalCountBuckets.last),
              fill: true,
              pointRadius: 0,
              type: 'line'
            }
          ],
          options: {
            ...baseOptions,
            scales: {
              ...baseOptions.scales,
              yAxes: [{ 
                 stacked: false, 
                 gridLines: { drawOnChartArea: false, drawBorder: true },
                 ticks: { maxTicksLimit: 2, beginAtZero: true, stepSize: 1 } 
              }] 
            }
          }
        }
      }
    }
  },
  methods: {
    moveChartDate(amount, unit) {
      this.chartReferenceDate = moment(this.chartReferenceDate).add(amount, unit)
      this.chartReferenceDate = moment(this.chartReferenceDate)
    },
    generateFakeInvoices() {
      const invoices = []
      const currentMonth = moment(this.chartReferenceDate)
      const lastMonth = moment(this.chartReferenceDate).clone().subtract(1, 'month')
      const overdueMonth = moment(this.chartReferenceDate).clone().subtract(2, 'month')

      // 1. Current Month Invoices (some paid, some outstanding)
      // Week 1
      invoices.push({ billingCycleStartDate: currentMonth.clone().startOf('month').add(1, 'days'), totalInvoicedAmount: 5000, amountPaid: 5000, paymentStatusId: 'Paid' })
      invoices.push({ billingCycleStartDate: currentMonth.clone().startOf('month').add(2, 'days'), totalInvoicedAmount: 2000, amountPaid: 0, paymentStatusId: 'Outstanding' })
      // Week 2
      invoices.push({ billingCycleStartDate: currentMonth.clone().startOf('month').add(8, 'days'), totalInvoicedAmount: 3000, amountPaid: 1500, paymentStatusId: 'Outstanding' })
      // Week 3
      invoices.push({ billingCycleStartDate: currentMonth.clone().startOf('month').add(16, 'days'), totalInvoicedAmount: 4000, amountPaid: 4000, paymentStatusId: 'Paid' })
      
      // 2. Last Month Invoices (for total count comparison)
      invoices.push({ billingCycleStartDate: lastMonth.clone().startOf('month').add(5, 'days'), totalInvoicedAmount: 2500, amountPaid: 2500, paymentStatusId: 'Paid' })
      invoices.push({ billingCycleStartDate: lastMonth.clone().startOf('month').add(15, 'days'), totalInvoicedAmount: 3000, amountPaid: 3000, paymentStatusId: 'Paid' })
      invoices.push({ billingCycleStartDate: lastMonth.clone().startOf('month').add(25, 'days'), totalInvoicedAmount: 1000, amountPaid: 1000, paymentStatusId: 'Paid' })

      // 3. Overdue Invoices (from 2 months ago, so they show in Global Pie but not Current Month bars)
      invoices.push({ billingCycleStartDate: overdueMonth.clone().startOf('month').add(10, 'days'), totalInvoicedAmount: 10000, amountPaid: 0, paymentStatusId: 'Overdue' })

      return invoices
    }
  }
}
</script>
